import { useRef, useEffect, useState, Fragment } from 'react';

import Box from '@mui/material/Box';

import { useTheme } from '@mui/material/styles';

import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { useParentSize } from '@visx/responsive';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';

import { EmptyBox } from 'shared/base';

import { MultiBarEntry } from './multiBarEntry';
import { MultiBarLegend } from './multiBarLegend';
import { BarChartTooltipContent } from './tooltipContent';
import { getBarChartMaxValue, getBarChartTotalValue, getBarHeight } from './utils';
import { XAxisLabel } from './xAxisLabel';
import {
  CHART_HOVER_OPACITY,
  BAR_CHART_BAR_LABEL_OFFSET_Y,
  BAR_CHART_BAR_PADDING,
  BAR_CHART_Y_AXIS_NUM_TICKS,
  BAR_CHART_Y_AXIS_OFFSET_X,
  BAR_CHART_Y_AXIS_OFFSET_Y,
  SVG_ANCILLARY_SPACE,
  BAR_WIDTH,
  CHART_CONTENT_HEIGHT,
  BAR_CHART_Y_AXIS_LABEL_OFFSET_X,
  CHART_HOVER_TRANSITION,
  NO_RESULTS_FOUND_MESSAGE,
} from '../../constants';
import { BarChartData } from '../../types';
import { bisectBarData, determineBarsToShow } from '../../utils/barUtils';
import { ChartTooltip } from '../tooltip/tooltip';
import { useChartTooltip } from '../tooltip/useChartTooltip';

interface Props {
  data: BarChartData;
  entryLabel?: string;
}

/**
 * BarChart component with fixed y-axis
 *
 * Features:
 * - Interactive tooltips on hover
 * - Dynamically limits visible bars based on available space
 * - Evenly distributes bars across available width
 */
export const BarChart = ({ data, entryLabel }: Props) => {
  const isMultiBar = typeof data?.[0]?.value === 'object';

  const { palette, typography } = useTheme();
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const yAxisRef = useRef<SVGSVGElement>(null);
  const [visibleData, setVisibleData] = useState<BarChartData>(data);
  const [secondaryData, setSecondaryData] = useState<BarChartData | undefined>();
  const [maxLabelWidth, setMaxLabelWidth] = useState<number>(0);
  const {
    TooltipInPortal,
    containerRef,
    handleLegendItemMouseOut,
    handleLegendItemMouseOver,
    handleMouseEnterSvg,
    handleMouseOutSvg,
    handleMouseOverSvg,
    hoveredLegendItem,
    popoverProps,
    tooltipData,
    tooltipProps,
  } = useChartTooltip<BarChartData[number]>({ anchorOn: 'enter' });

  const { height, parentRef, width } = useParentSize({ debounceTime: 150 });

  // Add extra space at the bottom for x-axis labels
  const BOTTOM_PADDING = 24;
  const innerHeight = height - SVG_ANCILLARY_SPACE - BOTTOM_PADDING;

  // Use the available container width
  const chartWidth = width - BAR_CHART_Y_AXIS_OFFSET_X - BAR_CHART_Y_AXIS_LABEL_OFFSET_X;

  // Calculate how many bars can fit based on longest label
  useEffect(() => {
    if (chartWidth <= 0 || data.length === 0) return;

    const { maxBars, maxLabelWidth } = determineBarsToShow(data, chartWidth);
    setMaxLabelWidth(maxLabelWidth);
    const { primaryData, secondaryData } = bisectBarData(data, maxBars);
    setSecondaryData(secondaryData);

    setVisibleData(primaryData);
  }, [chartWidth, data]);

  const totalValue = getBarChartTotalValue(visibleData);

  // Use the full chart width for the xScale instead of a calculated width based on fixed bar size
  const xScale = scaleBand({
    domain: visibleData.map((d) => d.name),
    padding: BAR_CHART_BAR_PADDING,
    range: [0, chartWidth], // Use full available width
    round: true,
  });

  const yScale = scaleLinear({
    domain: [0, getBarChartMaxValue(visibleData)],
    nice: true,
    range: [innerHeight, 0],
    round: true,
  });

  if (totalValue === 0) {
    return <EmptyBox title={NO_RESULTS_FOUND_MESSAGE} />;
  }

  // Calculate bar width based on the scale's bandwidth
  const barWidth = Math.min(xScale.bandwidth(), BAR_WIDTH);

  // Format the y-axis tick labels
  const formattedTicks = yScale.ticks(BAR_CHART_Y_AXIS_NUM_TICKS).map((value) => {
    return new Intl.NumberFormat('en-US', {
      compactDisplay: 'short',
      maximumFractionDigits: 1,
      notation: 'compact',
    }).format(Number(value));
  });

  return (
    <Box
      ref={parentRef}
      display="flex"
      height={CHART_CONTENT_HEIGHT}
      position="relative"
      width="100%"
    >
      {/* Fixed Y-axis container */}
      <Box height={height} position="relative" width={BAR_CHART_Y_AXIS_OFFSET_X}>
        <svg ref={yAxisRef} height={height} width={BAR_CHART_Y_AXIS_OFFSET_X}>
          {/* Y-axis tick labels */}
          {yScale.ticks(BAR_CHART_Y_AXIS_NUM_TICKS).map((value, index) => {
            const yPos = yScale(value) + BAR_CHART_Y_AXIS_OFFSET_Y;

            return (
              <text
                key={`y-tick-${index}`}
                alignmentBaseline="middle"
                fill={palette.text.secondary}
                fontSize={12}
                textAnchor="end"
                x={BAR_CHART_Y_AXIS_OFFSET_X - BAR_CHART_Y_AXIS_LABEL_OFFSET_X}
                y={yPos}
              >
                {formattedTicks[index]}
              </text>
            );
          })}
        </svg>
      </Box>

      {/* Chart content */}
      <Box
        ref={chartAreaRef}
        flexGrow={1}
        height={height}
        sx={{
          paddingBottom: `${BOTTOM_PADDING}px`,
        }}
      >
        <Box height={height - BOTTOM_PADDING} width={chartWidth}>
          <svg ref={containerRef} height={height} width={chartWidth}>
            <Group left={BAR_CHART_Y_AXIS_LABEL_OFFSET_X} top={BAR_CHART_Y_AXIS_OFFSET_Y}>
              <GridRows
                numTicks={BAR_CHART_Y_AXIS_NUM_TICKS}
                scale={yScale}
                stroke={palette.info.contrastText}
                strokeDasharray="3,3"
                strokeOpacity={0.2}
                width={chartWidth}
              />
              {visibleData.map((resource) => {
                const barHeight = getBarHeight(innerHeight, resource.value, yScale);
                const isItemHovered = hoveredLegendItem?.name === resource.name;
                const itemOpacity = hoveredLegendItem
                  ? isItemHovered
                    ? 1
                    : CHART_HOVER_OPACITY
                  : 1;

                return (
                  <Fragment key={`bar-${resource.name}`}>
                    {isMultiBar ? (
                      <MultiBarEntry
                        bars={Object.entries(resource.value)}
                        combinedBarWidth={barWidth}
                        handleMouseEnterSvg={handleMouseEnterSvg}
                        handleMouseOutSvg={handleMouseOutSvg}
                        handleMouseOverSvg={handleMouseOverSvg}
                        innerHeight={innerHeight}
                        itemOpacity={itemOpacity}
                        resource={resource}
                        xScale={xScale}
                        yScale={yScale}
                      />
                    ) : (
                      <>
                        <Bar
                          fill="transparent"
                          height={innerHeight}
                          onMouseEnter={(event) => handleMouseEnterSvg(event, resource)}
                          onMouseMove={(event) => handleMouseOverSvg(event, resource)}
                          onMouseOut={handleMouseOutSvg}
                          rx={2}
                          style={{
                            cursor: 'pointer',
                          }}
                          width={barWidth}
                          x={xScale(resource.name)}
                          y={0}
                        />
                        <Bar
                          fill={palette.primary.dark}
                          fillOpacity={itemOpacity}
                          height={barHeight}
                          onMouseEnter={(event) => handleMouseEnterSvg(event, resource)}
                          onMouseMove={(event) => handleMouseOverSvg(event, resource)}
                          onMouseOut={handleMouseOutSvg}
                          rx={2}
                          style={{
                            pointerEvents: 'none',
                            transition: CHART_HOVER_TRANSITION,
                          }}
                          width={barWidth}
                          x={xScale(resource.name)}
                          y={innerHeight - barHeight}
                        />
                      </>
                    )}
                    <XAxisLabel
                      handleLegendItemMouseOut={handleLegendItemMouseOut}
                      handleLegendItemMouseOver={handleLegendItemMouseOver}
                      hoveredLegendItem={hoveredLegendItem}
                      resource={resource}
                      theme={{ palette, typography }}
                      x={(xScale(resource.name) || 0) + barWidth / 2}
                      y={innerHeight + BAR_CHART_BAR_LABEL_OFFSET_Y}
                    />
                  </Fragment>
                );
              })}
            </Group>
          </svg>
          {isMultiBar && <MultiBarLegend keys={Object.keys(data[0].value)} />}
        </Box>
      </Box>
      <ChartTooltip
        popoverProps={popoverProps}
        TooltipInPortal={TooltipInPortal}
        tooltipProps={tooltipProps}
      >
        <BarChartTooltipContent
          entryLabel={entryLabel}
          maxLabelWidth={maxLabelWidth}
          secondaryData={secondaryData}
          tooltipData={tooltipData}
        />
      </ChartTooltip>
    </Box>
  );
};
